import React, { Fragment, useMemo, useState } from 'react';

import { Input } from '../../Form/Input/Input';
import { CenteredSpinner } from '../../CenteredSpinner/CenteredSpinner';
import { PresetItem } from '../PresetItem/PresetItem';
import { useAllSvpCategories } from '../../../hooks/useAllSvpCategories';
import { getFirstUnselectedResult } from '../helpers';

import type { SvpCategory } from '../../../types';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';

import styles from './CategoriesSearch.module.scss';

type Props = {
  selectedCategories: SvpCategory[];
  onAddClick: (category: SvpCategory) => void;
  onRemoveClick?: (category: SvpCategory) => void;
  label?: string;
};

export const CategoriesSearch: FC<Props> = ({
  selectedCategories = [],
  onAddClick,
  onRemoveClick,
  label = 'Search by title or ID',
}) => {
  const { data: categories = [], isLoading } = useAllSvpCategories();
  const [filter, setFilter] = useState('');

  const filteredCategories = useMemo(() => {
    const lowerCaseFilter = filter.toLowerCase();

    return (
      categories
        .filter(
          ({ title, id }) =>
            title.toLowerCase().includes(lowerCaseFilter) ||
            String(id).includes(lowerCaseFilter)
        )
        // Move archived categories to the bottom
        .sort((a, b) => Number(!a.showCategory) - Number(!b.showCategory))
    );
  }, [categories, filter]);

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    const selectedIds = selectedCategories.map(({ id }) => id);
    const nextResult = getFirstUnselectedResult(
      filteredCategories,
      selectedIds
    );

    if (nextResult) {
      onAddClick(nextResult);
      setFilter('');
    }
  }

  return (
    <Fragment>
      <Input
        label={label}
        name="categoryTitle"
        value={filter}
        disabled={isLoading}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setFilter(value);
        }}
        onKeyDown={onInputKeyDown}
        data-testid="preset-search-input"
      />
      <small className={styles.hint}>Press Enter to add the top result</small>
      {isLoading && <CenteredSpinner />}
      <ul className={styles.categoriesList} data-testid="preset-results">
        {filteredCategories.map((category) => {
          const isSelected = selectedCategories
            .map((presetItem) => presetItem.id)
            .includes(category.id);

          return (
            <li key={category.id}>
              <PresetItem
                item={category}
                dataType={'categories'}
                isSelected={isSelected}
                onClick={() =>
                  isSelected ? onRemoveClick?.(category) : onAddClick(category)
                }
              />
            </li>
          );
        })}
      </ul>
    </Fragment>
  );
};
